import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import AdminLayout from "../../components/app/AdminLayout";
import { Loader } from "./Dashboard";
import { Download, ChevronDown, ChevronRight, Gift } from "lucide-react";

export default function Payroll() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  useEffect(() => { adminApi.get("/admin/payroll").then(({ data }) => setData(data)); }, []);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Actor", "Shows Worked", "Bonus (£)", "Total (£)"]];
    data.rows.forEach((r) => rows.push([r.actor_name, r.shows_worked, (r.bonus_total || 0).toFixed(2), r.total.toFixed(2)]));
    const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `payroll-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const grandTotal = (data?.rows || []).reduce((s, r) => s + r.total, 0);
  const grandBonus = (data?.rows || []).reduce((s, r) => s + (r.bonus_total || 0), 0);

  return (
    <AdminLayout title="Payroll" subtitle="Earnings summary from sign-in records"
      actions={<button onClick={exportCsv} data-testid="payroll-export-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-blood hover:text-blood"><Download size={14} /> Export CSV</button>}>
      {!data ? <Loader/> : (
        <div className="border border-[#1a1a1a] bg-[#0A0A0A]">
          <table className="w-full text-sm" data-testid="payroll-table">
            <thead><tr className="border-b border-[#1a1a1a] text-left">
              <th className="px-4 py-3 label-mini">Actor</th>
              <th className="px-4 py-3 label-mini">Shows Worked</th>
              <th className="px-4 py-3 label-mini text-right">Bonus</th>
              <th className="px-4 py-3 label-mini text-right">Total Owed</th>
            </tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={4} className="py-12 text-center text-[#6B6862]">No payroll data yet — sign-ins will populate this view.</td></tr>}
              {data.rows.map((r) => (
                <React.Fragment key={r.actor_id}>
                  <tr className="border-b border-[#1a1a1a] hover:bg-[#101010] cursor-pointer" onClick={() => toggle(r.actor_id)} data-testid={`payroll-row-${r.actor_id}`}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2">
                        {expanded[r.actor_id] ? <ChevronDown size={13} className="text-[#6B6862]" /> : <ChevronRight size={13} className="text-[#6B6862]" />}
                        {r.actor_name}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono">{r.shows_worked}</td>
                    <td className="px-4 py-3 font-mono text-right" data-testid={`payroll-bonus-${r.actor_id}`}>{r.bonus_total > 0 ? <span className="text-[#3B82F6]">£{r.bonus_total.toFixed(2)}</span> : <span className="text-[#3a3835]">—</span>}</td>
                    <td className="px-4 py-3 font-mono text-right">£{r.total.toFixed(2)}</td>
                  </tr>
                  {expanded[r.actor_id] && (r.breakdown || []).map((b, i) => (
                    <tr key={`${r.actor_id}-${i}`} className="border-b border-[#121212] bg-[#070707] text-xs" data-testid={`payroll-line-${r.actor_id}-${i}`}>
                      <td className="px-4 py-2 pl-10 text-[#A19D94]">
                        {b.type === "bonus" ? (
                          <span className="inline-flex items-center gap-1.5 text-[#3B82F6]"><Gift size={11} /> Bonus — {b.name}{b.note ? ` (${b.note})` : ""}</span>
                        ) : b.name}
                      </td>
                      <td></td>
                      <td></td>
                      <td className="px-4 py-2 font-mono text-right text-[#A19D94]">£{(b.earned || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-[#1a1a1a]">
                <td className="px-4 py-3 font-display uppercase">Total</td>
                <td></td>
                <td className="px-4 py-3 text-right font-mono text-[#3B82F6]">£{grandBonus.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono text-blood text-lg">£{grandTotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
